import React from 'react';
import styled from 'styled-components';
import { MarkSpinner } from '../components/common/MarkSpinner';
import GlobalFooter from '../components/GlobalFooter';
import KeepCase from '../components/common/KeepCase';
import SeoHead from '../components/SeoHead';
import { WORKSTATION_BOOT_ESTIMATE, WORKSTATION_WAKE_ESTIMATE } from '../components/onboarding/JourneyStepper';
import { media } from '../styles/breakpoints';
import { useDesktopSession, describeSource, DESK_STATUS_COPY } from '../hooks/useDesktopSession';
import { liveMeterWords } from '../hooks/liveMeterModel';

// ─────────────────────────────────────────────────────────────────────────────
// /desktop now embeds the full Preston realtime avatar app from the user's
// account VM (or the internal Preston VM for Prest0 staff). This is an iframe
// integration, not a partial React rewrite — the realtime app owns the voice
// WebSocket, WebRTC lip-sync, mic capture, desktop launch APIs and noVNC view.
// See REALTIME_DESKTOP_NOTES.md for routing rules and smoke tests.
// ─────────────────────────────────────────────────────────────────────────────

// ─── Layout ───────────────────────────────────────────────────────────────────

/* Full-bleed on purpose (2026-08-14 owner directive: the desktop must use
   the whole landscape) — the embed's desktop pane absorbs extra width by
   design, so no ultrawide cap.

   2026-08-28 (Miles, avatar-desktop redesign) — two things went:
   • `min-height: 100vh`. The site nav sits ABOVE this page in normal flow,
     so a full-viewport minimum here guaranteed the page was taller than the
     screen before any content existed.
   • the 6.5rem/7rem bottom padding. GlobalFooter is the last child, so that
     padding was never clearing the chat bar for the frame — it was ~110px of
     empty page UNDER the footer. The footer carries its own 5-7rem bottom
     clearance, which is what actually keeps the chat bar off it. */
const Page = styled.div`
  background: var(--brand-canvas);
  color: var(--brand-text);
  display: flex;
  flex-direction: column;
  padding: 0.75rem 0.75rem 0;
  gap: 0.5rem;
  box-sizing: border-box;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  width: 100%;
  margin: 0 auto;

  ${media.tablet} {
    padding: 1rem 1rem 0;
    gap: 0.6rem;
  }

  ${media.laptop} {
    padding: 1rem 1.25rem 0;
  }
`;

/* 2026-08-28: the old header was a 62px block carrying "Prest0n" + "live
   desktop" above an embed whose own header already shows the wordmark and
   the tagline — the name was on screen four times. What survives is one
   slim row of things the embed CANNOT tell you: which workstation you are
   on, and the desktop-launch status. It keeps a fixed min-height so the
   status appearing mid-boot can never shove the live view down. */
const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  /* nowrap is load-bearing (2026-08-28 review): with wrap, a long status chip
     dropped to a second line on a phone and grew the row from 30px to ~62px
     mid-boot — shoving the live view down by exactly the amount this row
     exists to avoid. Both children shrink and ellipsize instead. */
  flex-wrap: nowrap;
  min-height: 30px;
  overflow: hidden;
`;

const SourcePill = styled.span<{ $source: 'internal' | 'account-vm' | 'none' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid
    ${({ $source }) =>
      $source === 'internal'
        ? 'var(--brand-gold)'
        : $source === 'account-vm'
        ? '#16a34a'
        : 'var(--brand-line)'};
  color: ${({ $source }) =>
    $source === 'internal'
      ? '#7a5f1f'
      : $source === 'account-vm'
      ? '#15803d'
      : '#777777'};
  background: ${({ $source }) =>
    $source === 'internal'
      ? 'rgba(198, 167, 94, 0.08)'
      : $source === 'account-vm'
      ? 'rgba(22, 163, 74, 0.06)'
      : 'rgba(0,0,0,0.02)'};
  white-space: nowrap;
  max-width: 100%;
  min-width: 0;
  flex-shrink: 0;
`;

/* Ellipsize the pill label rather than letting a long account id stretch or
   wrap the header row. */
const PillLabel = styled.span`
  /* inline-block, or overflow/ellipsis simply do not apply and a long account
     id stretches the pill and wraps the row — the exact failure this guards. */
  display: inline-block;
  max-width: 18ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PillDot = styled.span<{ $source: 'internal' | 'account-vm' | 'none' }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $source }) =>
    $source === 'internal'
      ? 'var(--brand-gold)'
      : $source === 'account-vm'
      ? '#22c55e'
      : '#bbb'};
`;

/* 2026-08-28 (Miles, avatar-desktop redesign) — the frame is now exactly ONE
   screenful, measured.

   What it replaces: `min-height: max(720px, calc(100vh - 215px))`. That floor
   existed because the embed laid itself out on a fixed 720px canvas and
   transform-scaled by min(1, height/720), so a shorter frame shrank all of
   its text. The embed no longer scales — it is fluid — so the floor's reason
   is gone, and the floor itself was half the problem: on a tall window the
   frame stretched to 100vh-215px while the embed still painted only 720px,
   leaving a screen of dead background above the footer.

   $stageHeight is measured in the component (viewport height minus this
   element's document offset minus the fixed chat bar's allowance) because
   the site nav and the trial banner sit above this page in normal flow and
   their combined height is not a constant any calc() can know.

   $embeddedHeight is kept for compatibility: a VM still serving the PREVIOUS
   page reports a portrait content height, and until its 5-minute page sync
   lands we must still grow around it. The new page reports null.

   $below (2026-10-17, chatBelow.ts): the chat stands under the frame where it
   cannot dock, and the frame is the screen (16:9 of its width, never under
   the screen's floor), with no floor of its own. */
const Frame = styled.div<{ $embeddedHeight?: number | null; $stageHeight?: number | null; $below?: boolean }>`
  flex: none;
  /* notched phones: the chat allowance above is measured in CSS pixels from
     the layout viewport, which knows nothing about the home indicator */
  margin-bottom: env(safe-area-inset-bottom, 0px);
  height: ${({ $stageHeight }) => ($stageHeight ? `${$stageHeight}px` : 'min(78dvh, 760px)')};
  min-height: ${({ $below }) => ($below ? '0' : '320px')};
  border-radius: 0.75rem;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid var(--brand-line);
  box-shadow: 0 0.375rem 1.25rem rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  position: relative;

  ${media.tablet} {
    border-radius: 1rem;
  }

  ${({ $embeddedHeight }) =>
    $embeddedHeight
      ? `
    height: ${$embeddedHeight}px;
    min-height: ${$embeddedHeight}px;
    overflow: visible;
  `
      : ''}
`;

const StyledIframe = styled.iframe<{ $embeddedHeight?: number | null }>`
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
  border: 0;
  display: block;

  ${({ $embeddedHeight }) =>
    $embeddedHeight
      ? `
    flex: none;
    height: ${$embeddedHeight}px;
    min-height: ${$embeddedHeight}px;
  `
      : ''}
`;

// ─── Loading + Error states ───────────────────────────────────────────────────

/* min-height:0 + overflow-y:auto (2026-08-28 review): as a flex child of the
   now-fixed-height Frame, `flex:1` alone leaves min-height:auto, so a card
   taller than the frame overflowed a container with overflow:hidden — the
   stalled-boot card lost its "check again" button off the bottom edge with no
   scrollbar. Padding shrinks on short frames so scrolling is the last resort. */
const StateCard = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: clamp(1rem, 4vh, 3rem) 1.5rem;
  text-align: center;
`;

/* The state cards' loader: the working mark (D-71), the logo's zero turning, at its largest size. */
const Spinner: React.FC = () => <MarkSpinner size="xl" />;

/* ── Live desktop status strip (2026-08-09 desktop-reliability pass) ─────────
   The embedded avatar app reports its desktop-launch lifecycle up via
   postMessage ({source:'preston-realtime', type:'desktopStatus'}); this strip
   mirrors it in the shell so progress stays visible even when the desktop
   pane inside the iframe is scrolled out of view (portrait phones). It
   renders nothing when the desktop is 'ready' — the live view speaks for
   itself — and never overlays the avatar. */
/* 2026-08-28: this was a full-width strip ABOVE the frame, so every boot
   mounted and unmounted a ~54px block and shoved the live view down and back
   up while the user watched. It is now an inline chip on the status row,
   which reserves its height whether or not the chip is present. */
const DeskStatusStrip = styled.div<{ $failed?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  border: 1px solid ${({ $failed }) => ($failed ? '#EAD9D6' : '#ECEAE5')};
  background: #ffffff;
  font-size: 0.8rem;
  color: #555555;
  flex-shrink: 1;
  min-width: 0;
`;

/* The status chip's: the mark at the size that sits beside a line of small words. */
const StripSpinner: React.FC = () => <MarkSpinner size="sm" />;

const DeskStatusText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DeskStatusButton = styled.button`
  background: none;
  border: 1px solid var(--brand-gold);
  color: #7a5f1f;
  border-radius: 999px;
  padding: 0.25rem 0.8rem;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;

  &:hover { background: rgba(198, 167, 94, 0.08); }
`;

const StateTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--brand-text);
  text-wrap: balance;

  ${media.laptop} {
    font-size: 1.2rem;
  }
`;

const StateMessage = styled.p`
  margin: 0;
  max-width: 36rem;
  color: #666666;
  font-size: 0.9rem;
  line-height: 1.5;
  text-wrap: pretty;

  ${media.laptop} {
    font-size: 0.95rem;
    max-width: 40rem;
  }
`;

/* Boot/stall desktop treatment (2026-08-03 desktop-polish audit): one narrow
   centered paragraph in a 70vh full-width frame read as a broken mobile page.
   At ≥1024px the status pairs with a "ready right now" panel so the empty
   frame becomes information instead of void. Mobile keeps the compact card. */
const BootSplit = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  /* BootSplit owns the scroll; its children must not open a second one. */
  & > * {
    overflow: visible;
    flex: 0 0 auto;
  }

  ${media.laptop} {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 400px);
    align-items: center;
    gap: 1.5rem;
    padding: 0 3rem;
  }
`;

const MeanwhilePanel = styled.aside`
  display: none;

  ${media.laptop} {
    display: block;
    background: #ffffff;
    border: 1px solid #ECEAE5;
    border-radius: 1rem;
    padding: 1.5rem 1.75rem;
    text-align: left;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.04);
  }
`;

const MeanwhileTitle = styled.h4`
  margin: 0 0 0.75rem;
  font-family: "Space Grotesk", "Inter", sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--brand-text);
  text-transform: lowercase;
`;

const MeanwhileList = styled.ul`
  /* the items name Prest0n; the ancestor's lowercase idiom must not downcase
     a proper noun (2026-08-22 owner directive) */
  text-transform: none;
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.6rem;
  font-size: 0.88rem;
  color: #555555;
  line-height: 1.5;
`;

const MeanwhileFoot = styled.p`
  margin: 0.9rem 0 0;
  font-size: 0.78rem;
  color: var(--brand-text-muted);
  line-height: 1.5;
  text-transform: lowercase;
`;

const RetryButton = styled.button`
  margin-top: 0.5rem;
  background: linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
  color: #ffffff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem 1.1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  transition: opacity 0.15s;

  &:hover { opacity: 0.9; }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

const NewTabLink = styled.a`
  font-size: 0.75rem;
  color: #777777;
  text-decoration: none;
  border-bottom: 1px dashed #cccccc;

  &:hover { color: var(--brand-text); }
`;

/* 2026-08-28: this panel and its four-item list were duplicated VERBATIM in
   the boot, stalled and asleep branches — three copies to keep in sync,
   differing only in the closing line. One component, one list. */
const READY_NOW: ReadonlyArray<string> = [
  '💬 chat with Prest0n — ask anything in the bar below',
  '🔍 deep research on any topic, market, or case',
  '📝 draft documents, letters, and proposals',
  '🌐 translate client communication instantly',
];

const ReadyNowPanel: React.FC<{ foot: React.ReactNode }> = ({ foot }) => (
  <MeanwhilePanel>
    <MeanwhileTitle>ready right now — no waiting</MeanwhileTitle>
    <MeanwhileList>
      {READY_NOW.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </MeanwhileList>
    <MeanwhileFoot>{foot}</MeanwhileFoot>
  </MeanwhilePanel>
);

// ─── Component ────────────────────────────────────────────────────────────────

const AgentActivity: React.FC = () => {
  const {
    target,
    embedSrc,
    iframeKey,
    bindIframe,
    onFrameLoad,
    frameRef,
    stageHeight,
    embeddedHeight,
    chatBelow,
    deskStatus,
    poolStrip,
    signInOwed,
    handleResumeSignIn,
    programWorkstation,
    handleOpenWorkstation,
    liveMeter,
    queueTicket,
    desktopHold,
    bootElapsedMin,
    bootStalled,
    bootStallReason,
    desktopWarming,
    frameStalled,
    wakeBusy,
    requestWake,
    handleRetry,
    handleFrameRetry,
  } = useDesktopSession();

  return (
    <>
      <SeoHead
        tabTitle="desktop"
        title="Desktop | prest0"
        description="Your Prest0n desktop — watch and interact in real time"
      />
      <Page>
        {/* 2026-08-13 (Miles): he's not "Prest0n avatar" or "Prest0n Desktop"
            — he's just Prest0n; keep header text minimal. 2026-08-28 takes
            that to its conclusion: the embed's own header already shows the
            wordmark, so this row carries only what the embed cannot know —
            which workstation you are on, and how its desktop is coming up.
            Both live on ONE fixed-height row so neither can shove the live
            view around while it boots. */}
        <StatusRow>
          <SourcePill $source={target.source}>
            <PillDot $source={target.source} />
            <PillLabel>{describeSource(target)}</PillLabel>
          </SourcePill>
          {poolStrip && (
            <DeskStatusStrip data-testid="workstation-pool-strip" title="your organization's workstations — counts only">
              <DeskStatusText>
                {`${poolStrip.total} workstations · ${poolStrip.up} up${poolStrip.waking > 0 ? ` · ${poolStrip.waking} waking` : ''}`}
              </DeskStatusText>
            </DeskStatusStrip>
          )}
          {deskStatus && (
            <DeskStatusStrip $failed={deskStatus === 'failed'} data-testid="desk-status-strip">
              {deskStatus !== 'failed' && <StripSpinner />}
              <DeskStatusText>{DESK_STATUS_COPY[deskStatus]}</DeskStatusText>
              {deskStatus === 'failed' && (
                <DeskStatusButton onClick={handleFrameRetry}>try again</DeskStatusButton>
              )}
            </DeskStatusStrip>
          )}
          {signInOwed && (
            <DeskStatusStrip data-testid="signin-owed-strip">
              <DeskStatusText>
                {signInOwed.status === 'signin-lost'
                  ? `${signInOwed.name} sign-in expired`
                  : `${signInOwed.name} sign-in unfinished`}
              </DeskStatusText>
              <DeskStatusButton data-testid="signin-owed-resume" onClick={handleResumeSignIn}>
                {`sign in to ${signInOwed.name}`}
              </DeskStatusButton>
            </DeskStatusStrip>
          )}
          {/* The workspace's Windows workstation (W2): a desktop program such as LexPI opens there, in the same
              window the Legal apps rows open. Never beside an owed sign-in (the hook decides: one directive). */}
          {programWorkstation && (
            <DeskStatusStrip data-testid="program-workstation-strip">
              <DeskStatusText>{`${programWorkstation.programName} workstation`}</DeskStatusText>
              <DeskStatusButton data-testid="program-workstation-open" onClick={handleOpenWorkstation}>
                open workstation
              </DeskStatusButton>
            </DeskStatusStrip>
          )}
          {/* The live meter (usage-metering step 11): this person's conversation with Prest0n and the workstations'
              awake time, in words both pages read from src/hooks/liveMeterModel.ts. */}
          {liveMeter && (
            <DeskStatusStrip data-testid="live-meter-strip">
              <DeskStatusText>{liveMeterWords(liveMeter)}</DeskStatusText>
            </DeskStatusStrip>
          )}
          {/* "Open in new tab" demoted out of the header (2026-08-09): the
              href is the raw VM URL + identity params — useful only as a
              fallback, so it now lives in the not-responding card below. */}
        </StatusRow>

        <Frame ref={frameRef} $embeddedHeight={embeddedHeight} $stageHeight={stageHeight} $below={chatBelow}>
          {target.loading && (
            <StateCard>
              <Spinner />
              <StateTitle>getting your desktop ready…</StateTitle>
              <StateMessage>connecting you to Prest0n.</StateMessage>
            </StateCard>
          )}

          {!target.loading && target.error && (
            <StateCard>
              <StateTitle>your desktop isn't available right now</StateTitle>
              <StateMessage>{target.error}</StateMessage>
              <RetryButton onClick={handleRetry}>try again</RetryButton>
            </StateCard>
          )}

          {!target.loading && !target.error && target.provisioning && !bootStalled && (
            <BootSplit>
              <StateCard data-testid="workspace-boot-card">
                <Spinner />
                <StateTitle>{desktopWarming ? 'your desktop is warming up' : 'your workstation is being built'}</StateTitle>
                <StateMessage>
                  {desktopWarming
                    ? 'almost there — the workstation is up and the desktop view is starting. this usually takes under a minute.'
                    : <>first-time setup takes {WORKSTATION_BOOT_ESTIMATE} — it happens once, and
                  you don't need to wait here.
                  {bootElapsedMin > 0 ? ` (about ${bootElapsedMin} minute${bootElapsedMin === 1 ? '' : 's'} in.)` : ''}</>}
                </StateMessage>
                <StateMessage>
                  chat is ready right now — ask Prest0n anything below while this
                  finishes. this tile switches to your live workstation
                  automatically the moment it's up.
                </StateMessage>
              </StateCard>
              <ReadyNowPanel
                foot={<>…and much more — <KeepCase>Prest0n</KeepCase> keeps learning your business as you go.</>}
              />
            </BootSplit>
          )}

          {/* A bootstrap that fails has no terminal state server-side, so
              without this the spinner counts minutes forever and the user is
              left guessing. Chat still works — say so, and give them a way
              out. */}
          {!target.loading && !target.error && target.provisioning && bootStalled && (
            <BootSplit>
              <StateCard data-testid="workspace-boot-stalled">
                <StateTitle>your workstation is taking longer than expected</StateTitle>
                <StateMessage>
                  setup usually finishes in {WORKSTATION_BOOT_ESTIMATE}
                  {bootElapsedMin > 0 ? `, and this one has been going for about ${bootElapsedMin} minutes` : ''} — it may need a hand.
                </StateMessage>
                {bootStallReason && (
                  <StateMessage data-testid="workspace-boot-stall-reason">
                    {bootStallReason === 'no-infra-doc' || bootStallReason === 'no-instance'
                      ? "we can't locate this organization's workstation record — our team can re-link it."
                      : bootStallReason === 'not-running'
                        ? "the workstation machine isn't running — it may be mid-replacement."
                        : bootStallReason === 'health-probe-failed' || bootStallReason === 'no-ip'
                          ? "the workstation isn't answering yet at its last known address."
                          : bootStallReason === 'missing-credentials' || bootStallReason === 'no-token'
                            ? 'the workstation is up but its secure link needs to be re-issued.'
                            : bootStallReason === 'avatar-page-not-serving'
                              ? 'the workstation is up — the live desktop view is still warming.'
                              : null}
                  </StateMessage>
                )}
                <StateMessage>
                  everything else still works: chat, research, drafting and documents
                  are ready below. reach out to your prest0 point of contact and
                  we'll get the workstation sorted.
                </StateMessage>
                <RetryButton onClick={handleRetry}>check again</RetryButton>
              </StateCard>
              <ReadyNowPanel foot="the workstation is only needed for live desktop tasks — nothing else is blocked while it finishes." />
            </BootSplit>
          )}

          {/* Idle-lifecycle cards (2026-08-18): asleep / waking / archived.
              An asleep doc still carries a stale baseUrl, so these render
              BEFORE any branch that could mount the iframe. */}
          {/* Resize at a stop boundary (2026-09-05, workstation plan PR-10): the
              shape sweep stops the machine for ~2 minutes to give it more
              room and wakes it in the same pass — never a sleep, never a
              wake button; the sweep owns the wake. */}
          {!target.loading && !target.error && (target.lifecycle === 'asleep' || target.lifecycle === 'waking') && target.stopReason === 'resize' && (
            <StateCard data-testid="workspace-resizing-card">
              <Spinner />
              <StateTitle>giving your workstation more room</StateTitle>
              <StateMessage>
                it's moving to a bigger machine — back in about 2 minutes, and
                everything stays exactly as you left it. this tile switches to
                your live workstation automatically.
              </StateMessage>
            </StateCard>
          )}

          {!target.loading && !target.error && target.lifecycle === 'asleep' && target.stopReason !== 'billing' && target.stopReason !== 'resize' && (
            <BootSplit>
              <StateCard data-testid="workspace-asleep-card">
                <StateTitle>🌙 your workstation is asleep</StateTitle>
                <StateMessage>
                  it went to sleep after a quiet stretch to save resources.
                  nothing was lost — you're still signed in to everything on it.
                </StateMessage>
                <StateMessage>
                  waking it takes {WORKSTATION_WAKE_ESTIMATE}, and it starts
                  automatically the moment you ask Prest0n for anything that
                  needs the desktop.
                </StateMessage>
                <RetryButton onClick={() => void requestWake('button')} disabled={wakeBusy}>
                  {wakeBusy ? 'waking…' : 'wake it up'}
                </RetryButton>
              </StateCard>
              <ReadyNowPanel foot="the workstation is only needed for live desktop tasks — nothing else sleeps." />
            </BootSplit>
          )}

          {!target.loading && !target.error && target.lifecycle === 'asleep' && target.stopReason === 'billing' && (
            <StateCard data-testid="workspace-billing-paused-card">
              <StateTitle>your workstation is paused</StateTitle>
              <StateMessage>
                it's paused because of a billing issue. update your payment
                method and it comes back automatically — nothing was deleted.
              </StateMessage>
              <NewTabLink href="/subscription">manage subscription</NewTabLink>
            </StateCard>
          )}

          {!target.loading && !target.error && target.lifecycle === 'waking' && target.stopReason !== 'resize' && (
            <StateCard data-testid="workspace-waking-card">
              <Spinner />
              <StateTitle>waking your workstation</StateTitle>
              <StateMessage>
                everything is exactly as you left it — this takes {WORKSTATION_WAKE_ESTIMATE}.
                this tile switches to your live workstation automatically.
              </StateMessage>
              {bootStalled && (
                <>
                  <StateMessage>
                    it's taking longer than expected — chat and everything else
                    still work below.
                  </StateMessage>
                  <RetryButton onClick={handleRetry}>check again</RetryButton>
                </>
              )}
            </StateCard>
          )}

          {/* Rebuild from snapshot (2026-09-05, workstation plan PR-10): an
              archived workstation coming back — several minutes, not 45–90 s. */}
          {!target.loading && !target.error && target.lifecycle === 'rebuilding' && (
            <StateCard data-testid="workspace-rebuilding-card">
              <Spinner />
              <StateTitle>rebuilding your workstation</StateTitle>
              <StateMessage>
                it's coming back from its saved snapshot — this takes several
                minutes, and most of your sign-ins come right back. this tile
                switches to your live workstation automatically.
              </StateMessage>
              {bootStalled && (
                <>
                  <StateMessage>
                    it's taking longer than expected — chat and everything else
                    still work below.
                  </StateMessage>
                  <RetryButton onClick={handleRetry}>check again</RetryButton>
                </>
              )}
            </StateCard>
          )}

          {!target.loading && !target.error && target.lifecycle === 'archived' && target.stopReason !== 'billing' && (
            <StateCard data-testid="workspace-archived-card">
              <StateTitle>your workstation was archived</StateTitle>
              <StateMessage>
                after a long quiet stretch it was archived to save resources —
                everything important is saved. rebuilding takes {WORKSTATION_BOOT_ESTIMATE},
                and most of your sign-ins come right back with it.
              </StateMessage>
              {/* One-click rebuild: the wake seam answers 'rebuilding' on the
                  self-serve path (uid-plane always; org-plane by DEFAULT since
                  2026-08-18 — orgRebuildEnabled:false is the off switch) and
                  'archived' only when rebuild is disabled or failed to start —
                  then nothing changes here and the support line below covers it. */}
              <RetryButton onClick={() => void requestWake('button')} disabled={wakeBusy}>
                {wakeBusy ? 'rebuilding…' : 'rebuild it'}
              </RetryButton>
              <StateMessage>
                if the button doesn't kick things off, reach out to your prest0
                point of contact and we'll bring it back for you.
              </StateMessage>
            </StateCard>
          )}

          {!target.loading && !target.error && target.lifecycle === 'archived' && target.stopReason === 'billing' && (
            <StateCard data-testid="workspace-archived-billing-card">
              <StateTitle>your workstation is archived</StateTitle>
              <StateMessage>
                it was archived because of a billing issue. update your payment
                method and we'll bring it back — everything important is saved.
              </StateMessage>
              <NewTabLink href="/subscription">manage subscription</NewTabLink>
            </StateCard>
          )}

          {/* Queued for a desktop slot (2026-09-05, workstation plan PR-10):
              the viewer's chat task is waiting for a teammate's session to
              free. Facts from their own ticket; the sweep resumes the task
              and this tile takes the desktop the moment it opens. */}
          {!target.loading && !target.error && !target.provisioning && target.lifecycle === 'active' && queueTicket && (
            <StateCard data-testid="workspace-queued-card">
              <Spinner />
              {queueTicket.status === 'summoned' ? (
                <>
                  <StateTitle>your turn — starting your task now</StateTitle>
                  <StateMessage>
                    a workstation slot just freed up. <KeepCase>Prest0n</KeepCase> is picking
                    your task back up; this tile switches to your live desktop as
                    soon as it opens.
                  </StateMessage>
                </>
              ) : queueTicket.scaling ? (
                <>
                  {/* Scaling (2026-09-05, workstation plan PR-13b): every workstation
                      is busy and the platform is bringing another of the
                      organization's own up for this task — a wake (~90 s) or a
                      new member (~10 min). Facts from the ticket; nothing decided here. */}
                  <StateTitle>
                    {queueTicket.scaling.action === 'waking'
                      ? 'bringing a second workstation up for you'
                      : 'adding a workstation for your team'}
                  </StateTitle>
                  <StateMessage>
                    every workstation is busy right now, so
                    {queueTicket.scaling.action === 'waking' ? ' another one is waking up' : ' a new one is being set up'}
                    {queueTicket.scaling.etaSeconds
                      ? ` — about ${queueTicket.scaling.etaSeconds < 120 ? `${Math.max(30, Math.round(queueTicket.scaling.etaSeconds / 30) * 30)} seconds` : `${Math.max(1, Math.round(queueTicket.scaling.etaSeconds / 60))} min`}`
                      : ''}.
                    this tile switches to it automatically the moment it's ready, and your
                    task picks up on its own — no need to send it again.
                  </StateMessage>
                  <StateMessage>
                    chat and everything else work right now — nothing else is blocked.
                  </StateMessage>
                </>
              ) : (
                <>
                  <StateTitle>
                    {queueTicket.position ? `you're #${queueTicket.position} in line for the workstation` : "you're in line for the workstation"}
                  </StateTitle>
                  <StateMessage>
                    a teammate is using it right now
                    {queueTicket.etaSeconds ? ` — about ${Math.max(1, Math.round(queueTicket.etaSeconds / 60))} min` : ''}.
                    this tile switches over automatically the moment it frees, and your
                    task picks up on its own — no need to send it again.
                  </StateMessage>
                  <StateMessage>
                    chat and everything else work right now — nothing else is blocked.
                  </StateMessage>
                </>
              )}
            </StateCard>
          )}

          {/* Arrival hold (2026-08-20): the account is promoted but the desktop
              renderer hasn't answered yet — show the honest warming card
              instead of mounting an iframe that can only spin. Releases the
              moment the server's desktop probe passes (or 90s fail-open). */}
          {!target.loading && !target.error && !target.provisioning && !queueTicket && desktopHold && target.targetUrl && (
            <StateCard data-testid="workspace-desktop-hold">
              <Spinner />
              <StateTitle>your desktop is warming up</StateTitle>
              <StateMessage>
                almost there — the workstation is up and the desktop view is
                starting. this usually takes under a minute, and this tile
                switches to your live desktop automatically.
              </StateMessage>
            </StateCard>
          )}

          {!target.loading && !target.error && !target.provisioning && !queueTicket && !desktopHold && target.targetUrl && frameStalled && (
            <StateCard data-testid="workspace-frame-stalled">
              <StateTitle>workstation not responding yet</StateTitle>
              <StateMessage>
                your workstation isn't answering — it may still be waking up.
              </StateMessage>
              <RetryButton onClick={handleFrameRetry}>try again</RetryButton>
              <NewTabLink
                href={target.targetUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                or open the workstation in a new tab
              </NewTabLink>
            </StateCard>
          )}

          {!target.loading && !target.error && !target.provisioning && !queueTicket && !desktopHold && target.targetUrl && !frameStalled && (
            <StyledIframe
              key={iframeKey}
              ref={bindIframe}
              $embeddedHeight={embeddedHeight}
              title="Prest0n desktop"
              src={embedSrc ?? target.targetUrl}
              onLoad={onFrameLoad}
              // First-party VM app — needs mic for voice WS, WebRTC for
              // lip-sync, autoplay for PCM playback, fullscreen for noVNC
              // expand, clipboard for desktop ops.
              allow="microphone; camera; autoplay; fullscreen; clipboard-read; clipboard-write"
              // sandbox intentionally omitted: the embedded VM app needs
              // unrestricted access to its own origin for WebSocket auth,
              // service workers, and noVNC. The VM app is first-party
              // Prest0n, so the sandbox provides no real safety here while
              // breaking mic/WebRTC/WS in practice. See REALTIME_DESKTOP_NOTES.md.
            />
          )}
        </Frame>

        <GlobalFooter />
      </Page>
    </>
  );
};

export default AgentActivity;
